import { computed, inject, Injectable, signal } from '@angular/core';
import { addMonths, endOfToday, isAfter } from 'date-fns';
import { combineLatest, map, of, switchMap } from 'rxjs';
import { Contractor } from '../models/contractor.model';
import { User } from '../models/user.model';
import { partnerExpiresMonths, partnerPercentage } from '../utility/constants';
import { Roles } from '../utility/role';
import { convertTimestamp } from '../utility/time';
import { ContractorsService } from './contractors.service';
import { ProductsService } from './products.service';
import { UsersService } from './users.service';

@Injectable()
export class PartnerService {
  private contractorsService = inject(ContractorsService);
  private productsService = inject(ProductsService);
  private usersService = inject(UsersService);

  private calcContractorIncome(contractor: Contractor) {
    const expires = addMonths(convertTimestamp(contractor.created), partnerExpiresMonths);
    const expired = isAfter(endOfToday(), expires);
    return expired ? of(signal(0)) : of(contractor).pipe(
      switchMap((item) => combineLatest([this.usersService.getListFromDb({ contractor: item.guid }), this.productsService.getList()])),
      map(([users, products]) => {
        const admins = users.filter((u) => !u.inactive && [Roles.admin].includes(u.role) && !u.rights?.supervisor);
        const supervisors = users.filter((u) => !u.inactive && [Roles.admin].includes(u.role) && u.rights?.supervisor);
        const workers = users.filter((u) => !u.inactive && [Roles.worker, Roles.storage].includes(u.role));
        const adminPrice = contractor.license === 'free' ? products.find((it) => it.metadata?.role === Roles.admin)?.price ?? signal(0) : signal(contractor.prices?.admin ?? 0);
        const supervisorPrice = contractor.license === 'free'
          ? products.find((it) => it.metadata?.role === Roles.supervisor)?.price ?? signal(0)
          : signal(contractor.prices?.supervisor ?? 0);
        const workerPrice = contractor.license === 'free' ?
          products.find((it) => it.metadata?.role === Roles.worker)?.price ?? signal(0) : signal(contractor.prices?.worker ?? 0);
        const adminTotalPrice = computed(() => contractor.licenses?.admin ? contractor.licenses.admin * contractor.prices.admin : admins.length * adminPrice());
        const supervisorTotalPrice = computed(() =>
          contractor.licenses?.supervisor ? contractor.licenses.supervisor * contractor.prices.supervisor : supervisors.length * supervisorPrice());
        const workersTotalPrice = computed(() => contractor.licenses?.worker ? contractor.licenses.worker * contractor.prices.worker : workers.length * workerPrice());
        const total = computed(() => (adminTotalPrice() + supervisorTotalPrice() + workersTotalPrice()) * partnerPercentage);
        return total;
      }),
    );
  }

  calculatePartnersIncome(user: User) {
    return user.rights?.premiumPartner && user.partners?.length > 0 ? of(user).pipe(
      switchMap((item) => this.usersService.getListFromDb({ guids: item.partners })),
      map((users) => users.map((partner) => this.calculateIncome(partner))),
      map((items) => combineLatest(items)),
      switchMap((items) => items),
      map((items) => items.reduce((prev, curr) => prev + (curr * partnerPercentage), 0)),
    ) : of(0);
  }

  calculateIncome(user: User) {
    return user.contractors?.length > 0 ? of(user).pipe(
      switchMap((item) => this.contractorsService.getList({ guids: item.contractors })),
      map((contractors) => contractors.map((c) => this.calcContractorIncome(c))),
      map((items) => combineLatest(items)),
      switchMap((incomes) => incomes),
      map((incomes) => incomes.reduce((prev, curr) => prev + curr(), 0)),
    ) : user.role === Roles.superAdmin ? this.contractorsService.getList().pipe(
      map((contractors) => contractors.map((c) => this.calcContractorIncome(c))),
      map((items) => combineLatest(items)),
      switchMap((incomes) => incomes),
      map((incomes) => incomes.reduce((prev, curr) => prev + curr(), 0)),
    ) : of(0);
  }

  calculateTotalIncome(user: User) {
    return of(user).pipe(
      switchMap((item) => combineLatest([this.calculateIncome(item), this.calculatePartnersIncome(item)])),
      map(([income, partnersIncome]) => income + partnersIncome),
    );
  }
}
